import {
  CalendarToday as CalendarIcon,
  Check as CheckIcon,
  DirectionsBus as BusIcon,
  Flight as AirplaneIcon,
  Map as MapIcon,
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { storageUrlWithCredentials } from "../components/imagesPreview/storageUrlWithCredentials";
import { groupTagsByCategory, TripTagCategory } from "../components/tagSelect/tags";
import CardImage from "./CardImage";
import type { ListPublicPackagesResponseItem } from "./publicPackagesApi";

interface Tag {
  readonly category: string;
  readonly value: string;
}

const Duration: FC<{ days: number }> = ({ days }) => (
  <Grid container>
    <Grid size={3}>
      <CalendarIcon />
    </Grid>
    <Grid size={9}>
      <Typography>
        {days} {days === 1 ? "dzień" : "dni"}
      </Typography>
    </Grid>
  </Grid>
);

const ModeOfTransportation: FC<{ tags: Tag[] }> = ({ tags }) => {
  const modeOfTransportation = groupTagsByCategory(tags)["Transport"]?.at(0);
  if (!modeOfTransportation) {
    return null;
  }
  return (
    <Grid container>
      <Grid size={3}>{modeOfTransportation === "Samolot" ? <AirplaneIcon /> : <BusIcon />}</Grid>
      <Grid size={9}>
        <Typography>{modeOfTransportation}</Typography>
      </Grid>
    </Grid>
  );
};

const Map: FC<{ mapUrl: string | undefined }> = ({ mapUrl }) => {
  const [signedUrl, setSignedUrl] = useState<string>();
  useEffect(() => {
    if (mapUrl) {
      storageUrlWithCredentials(mapUrl).then((url) => setSignedUrl(url));
    } else {
      setSignedUrl(undefined);
    }
  }, [mapUrl]);

  if (!mapUrl) {
    return null;
  }
  return (
    <Grid container>
      <Grid size={3}>
        <MapIcon />
      </Grid>
      <Grid size={9}>
        <Link href={signedUrl} underline="none" color="inherit">
          Mapa
        </Link>
      </Grid>
    </Grid>
  );
};

const TagGroupList: FC<{ tags: Tag[]; label: string; category: string }> = ({ tags, category, label }) => {
  const countries = groupTagsByCategory(tags)[category] ?? [];
  if (countries.length === 0) {
    return null;
  }
  return (
    <List dense subheader={<ListSubheader>{label}</ListSubheader>}>
      {countries.map((c) => (
        <ListItem key={c} dense sx={{ paddingBottom: 0, paddingTop: 0 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <CheckIcon />
          </ListItemIcon>
          <ListItemText>{c}</ListItemText>
        </ListItem>
      ))}
    </List>
  );
};

interface Props {
  readonly value: ListPublicPackagesResponseItem;
}

const PackageCard: FC<Props> = ({ value }) => {
  const navigate = useNavigate();
  const packageUserFriendlyId = value.packageName.substring(0, 3);
  return (
    <Card className="entity-card" sx={{ display: "flex" }}>
      <CardImage image={value.images.at(0)} margin={8} />
      <Stack
        direction="row"
        spacing={2}
        divider={<Divider orientation="vertical" flexItem />}
        sx={{
          flexGrow: 1,
          margin: 2,
        }}
      >
        <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Typography variant="h6">{value.name}</Typography>
          <Typography>{value.packageName}</Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "flex-end",
              flexGrow: 1,
            }}
          >
            <TagGroupList label="Odwiedzane kraje:" category={TripTagCategory.COUNTRY} tags={value.tags} />
            <TagGroupList label="Polecamy dla grup:" category={TripTagCategory.GROUP_TYPE} tags={value.tags} />
          </Stack>
        </CardContent>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            flexShrink: 0,
            flexGrow: 0,
          }}
        >
          <Stack spacing={1}>
            <Duration days={value.numberOfDays} />
            <ModeOfTransportation tags={value.tags} />
            <Map mapUrl={value.mapUrl} />
          </Stack>
          <CardActions>
            <Stack direction="column" spacing={1}>
              <Button variant="outlined" size="small" fullWidth onClick={() => navigate(value.id.id)} color="primary">
                Zobacz szczegóły
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                fullWidth
                onClick={() => navigate(`/e-commerce?query=${packageUserFriendlyId}`)}
              >
                Sprawdź w e-commerce
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                fullWidth
                onClick={() => navigate(`/calculations/new?fromPackage=${value.id.id}`)}
              >
                Wyceń pakiet
              </Button>
            </Stack>
          </CardActions>
        </Stack>
      </Stack>
    </Card>
  );
};

export default PackageCard;
